'use client';

import InlineStyle from '@/components/InlineStyle';
import Link from 'next/link';
import { motion } from 'framer-motion';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, Button, FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import PlatformHeroPanel from '@/components/platform/PlatformHeroPanel';
import { slideInLeft, slideInRight, scaleIn, viewportOnce, spring, springSoft } from '@/lib/motion';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Radio,
  Megaphone,
  AlertOctagon,
  UserCheck,
  Globe,
  Activity,
  Sparkles,
  Eye,
  Users,
  ShieldAlert,
  Layers,
  Mic,
  Check,
} from 'lucide-react';

/* metadata in ./layout.js — this page is a client component for i18n + motion. */

/* Tints — all derived from the brand sky-blue (#0098FF) family. */
const TINTS = {
  blue:   { bg: '#EAF6FF', stroke: '#BFE3FF', accent: '#0098FF' },
  sky:    { bg: '#EAF3FF', stroke: '#C7DEFF', accent: '#2E90FF' },
  violet: { bg: '#E9EFFF', stroke: '#C6D6FF', accent: '#0057D4' },
  ice:    { bg: '#F2F9FF', stroke: '#D7EEFF', accent: '#5AB4FF' },
};

/* ── Deep-dive background SVGs ──────────────────────────────────────────── */
function BrandHealthBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55 }} aria-hidden="true">
      <defs>
        <linearGradient id={`bh-area-${tint.accent.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.30" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="40" y1="360" x2="360" y2="360" stroke={tint.accent} strokeOpacity="0.25" strokeWidth="1" />
      {[
        [60, 18], [90, 26], [120, 22], [150, 34], [180, 28],
        [210, 40], [240, 32], [270, 46], [300, 38], [330, 52],
      ].map(([x, h], i) => (
        <rect key={i} x={x - 7} y={360 - h} width="14" height={h} rx="2" fill={tint.accent} fillOpacity="0.18" />
      ))}
      <path
        d="M 40 320 L 90 305 L 140 312 L 190 295 L 240 282 L 290 270 L 340 285 L 340 360 L 40 360 Z"
        fill={`url(#bh-area-${tint.accent.slice(1)})`}
      />
      <path
        d="M 40 320 L 90 305 L 140 312 L 190 295 L 240 282 L 290 270 L 340 285"
        fill="none"
        stroke={tint.accent}
        strokeOpacity="0.85"
        strokeWidth="2.2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx="290" cy="270" r="4.5" fill={tint.accent} />
    </svg>
  );
}

function CampaignTileBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.6 }} aria-hidden="true">
      <rect x="60" y="280" width="280" height="100" rx="12" fill="#fff" stroke={tint.accent} strokeOpacity="0.40" />
      <rect x="76" y="296" width="130" height="6" rx="3" fill={tint.accent} fillOpacity="0.55" />
      <rect x="216" y="296" width="56" height="6" rx="3" fill={tint.accent} fillOpacity="0.25" />
      {[
        { x: 76,  v: 0.85 },
        { x: 168, v: 0.55 },
        { x: 260, v: 0.40 },
      ].map((s, i) => (
        <g key={i}>
          <rect x={s.x} y="318" width="68" height="50" rx="8" fill={tint.accent} fillOpacity="0.10" stroke={tint.accent} strokeOpacity="0.30" />
          <rect x={s.x + 10} y="328" width="34" height="4" rx="2" fill={tint.accent} fillOpacity="0.30" />
          <rect x={s.x + 10} y="338" width="48" height="11" rx="2" fill={tint.accent} fillOpacity={s.v} />
          <rect x={s.x + 10} y="354" width="28" height="4" rx="2" fill={tint.accent} fillOpacity="0.25" />
        </g>
      ))}
    </svg>
  );
}

function CrisisSpikeBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.6 }} aria-hidden="true">
      <line x1="40" y1="360" x2="360" y2="360" stroke={tint.accent} strokeOpacity="0.25" strokeWidth="1" />
      <line x1="40" y1="320" x2="360" y2="320" stroke={tint.accent} strokeOpacity="0.45" strokeWidth="1.2" strokeDasharray="5 4" />
      <path
        d="M 40 305 L 100 300 L 160 295 L 220 305 L 240 285 L 260 270 L 280 250 L 340 290 L 340 360 L 40 360 Z"
        fill={tint.accent}
        fillOpacity="0.18"
      />
      <path
        d="M 40 305 L 100 300 L 160 295 L 220 305 L 240 285 L 260 270 L 280 250 L 340 290"
        fill="none"
        stroke={tint.accent}
        strokeOpacity="0.85"
        strokeWidth="2.2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx="280" cy="250" r="6" fill={tint.accent} />
      <circle cx="280" cy="250" r="13" fill="none" stroke={tint.accent} strokeOpacity="0.45" strokeWidth="1.2" />
      <circle cx="280" cy="250" r="22" fill="none" stroke={tint.accent} strokeOpacity="0.20" strokeWidth="1" />
    </svg>
  );
}

function InfluencerRowBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.6 }} aria-hidden="true">
      {[
        { y: 280, fill: 0.85, bar: 200 },
        { y: 308, fill: 0.65, bar: 160 },
        { y: 336, fill: 0.45, bar: 120 },
      ].map((r, i) => (
        <g key={i}>
          <circle cx="68" cy={r.y + 14} r="11" fill={tint.accent} fillOpacity={r.fill} />
          <rect x="86" y={r.y + 8} width="70" height="5" rx="2" fill={tint.accent} fillOpacity="0.55" />
          <rect x="86" y={r.y + 17} width="50" height="4" rx="2" fill={tint.accent} fillOpacity="0.25" />
          <rect x="170" y={r.y + 10} width={r.bar} height="10" rx="5" fill={tint.accent} fillOpacity={r.fill * 0.7} />
        </g>
      ))}
    </svg>
  );
}

function GlobeGridBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55 }} aria-hidden="true">
      <defs>
        <radialGradient id={`gg-glow-${tint.accent.slice(1)}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.08" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="200" cy="350" r="90" fill={`url(#gg-glow-${tint.accent.slice(1)})`} />
      <circle cx="200" cy="350" r="60" fill="none" stroke={tint.accent} strokeOpacity="0.45" strokeWidth="1.2" />
      <ellipse cx="200" cy="350" rx="20" ry="60" fill="none" stroke={tint.accent} strokeOpacity="0.25" />
      <ellipse cx="200" cy="350" rx="40" ry="60" fill="none" stroke={tint.accent} strokeOpacity="0.30" />
      <ellipse cx="200" cy="350" rx="60" ry="14" fill="none" stroke={tint.accent} strokeOpacity="0.30" />
      <ellipse cx="200" cy="350" rx="60" ry="40" fill="none" stroke={tint.accent} strokeOpacity="0.25" />
      {[
        { x: 175, y: 332, r: 4, ring: 12 },
        { x: 225, y: 340, r: 3, ring: 9 },
        { x: 195, y: 370, r: 4, ring: 11 },
      ].map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={p.ring} fill="none" stroke={tint.accent} strokeOpacity="0.4" strokeWidth="1" />
          <circle cx={p.x} cy={p.y} r={p.r} fill={tint.accent} />
        </g>
      ))}
    </svg>
  );
}

/* ── Content ────────────────────────────────────────────────────────────── */
const HERO = {
  eyebrow: 'Platform Capability',
  title: 'Command Centre',
  tagline: 'The Social World Is Talking. Are You Watching Closely Enough?',
  body: [
    'The Locobuzz Digital Command Centre is your brand’s mission control — a real-time view into digital conversations, campaign performance, influencer activity, and customer issues, all in one powerful command hub.',
    'Whether it’s a campaign launch, a PR storm, or a product issue, your teams will always see it coming.',
  ],
};

/* DRAFT — review */
const IMPACT = [
  { value: '500K+', label: 'Alerts processed every day' },
  { value: '< 2 min', label: 'Average crisis detection time' },
  { value: '60%', label: 'Faster cross-team response' },
  { value: '30+', label: 'Unified data sources in one view' },
];

const STACK_POINTS = [
  { Icon: Eye,         title: 'Live Decision Data',        text: 'Make smarter decisions with live data on sentiment, volume spikes, influencers, and campaign traction.', tint: 'blue' },
  { Icon: Users,       title: 'Competitor Watch',           text: 'Watch what competitors are doing and how audiences are reacting — on one screen.',                       tint: 'violet' },
  { Icon: Layers,      title: 'Break the Silos',            text: 'CX, marketing, PR, and product all operate on the same real-time truth.',                                tint: 'sky' },
  { Icon: ShieldAlert, title: 'Risk Coordination',          text: 'Detect reputational risks as they surface, and coordinate team actions for rapid response.',             tint: 'blue' },
  { Icon: Activity,    title: 'Mid-Flight Course-Correct',  text: 'Monitor creative performance, engagement metrics, and course-correct content in real time.',             tint: 'violet' },
  { Icon: Mic,         title: 'Voice-Activated War Room',   text: 'Fully scalable, voice activated — built to power war rooms, campaign hubs, and digital HQs.',           tint: 'sky' },
];

const DEEP_DIVES = [
  {
    eyebrow: 'Brand Health',
    title: 'Monitor Brand Health at a Glance',
    lead: 'Track brand, product, and industry conversations as they happen — on social, news, forums, and beyond.',
    bullets: [
      'Track sentiment shifts, mention spikes, and key issue trends across all digital channels',
      'Get an immediate overview of brand performance segmented by location, category, and channel',
      'Visualize chatter volume, audience reactions, and evolving narratives in real time',
      'Merge internal metrics like GA, chatbot, and CRM to see the full brand pulse on one screen',
    ],
    cta: { label: 'Start Listening', href: '#book-demo' },
    visual: { Icon: Radio, title: 'Brand Pulse', subtitle: 'Sentiment · Volume · Narrative', Bg: BrandHealthBg, tint: 'blue' },
  },
  {
    eyebrow: 'Campaigns',
    title: 'Track Campaigns as They Perform',
    lead: 'Watch reach, engagement, and sentiment evolve as your campaign runs — and steer it before it drifts.',
    bullets: [
      'Monitor campaign metrics — reach, engagement, CTRs, and sentiment — in real time',
      'Filter by platform, asset, audience, or region to identify what’s resonating and what’s not',
      'Spot campaign fatigue, creative underperformance, or feedback spikes instantly',
      'Enable agile creative decisions with mid-flight optimizations that improve ROI',
    ],
    cta: { label: 'Know More', href: '#book-demo' },
    visual: { Icon: Megaphone, title: 'Campaign Console', subtitle: 'Reach · Engagement · CTR', Bg: CampaignTileBg, tint: 'violet' },
    reverse: true,
    dark: true,
  },
  {
    eyebrow: 'Crisis Detection',
    title: 'Get Ahead of Every Crisis',
    lead: 'Spot reputation risk the moment it forms — and bring the right teams together before it spreads.',
    bullets: [
      'Detect volume surges, negative sentiment spikes, and viral risks as they happen',
      'Set automated triggers and thresholds for early-warning alerts across teams',
      'Drill into the origin of issues and understand the velocity and spread of narratives',
      'Align PR, CX, and marketing teams with a unified, real-time view of the situation',
    ],
    cta: { label: 'Measure Campaigns', href: '#book-demo' },
    visual: { Icon: AlertOctagon, title: 'Crisis Watch', subtitle: 'Thresholds · Triggers · Origin', Bg: CrisisSpikeBg, tint: 'sky' },
  },
  {
    eyebrow: 'Influencer Impact',
    title: 'Monitor Influencer Impact for Campaigns',
    lead: 'See whose voice is moving the needle — and decide where to amplify or push back.',
    bullets: [
      'View influencer posts, videos, and engagement in real time as conversations unfold',
      'Differentiate brand advocates, micro-influencers, and critics by reach and relevance',
      'Analyze tone, content type, and audience reaction to detect positive and negative mentions',
      'Activate or respond to key voices based on campaign objectives or brand triggers',
    ],
    visual: { Icon: UserCheck, title: 'Voice Leaderboard', subtitle: 'Reach · Relevance · Tone', Bg: InfluencerRowBg, tint: 'blue' },
    reverse: true,
  },
  {
    eyebrow: 'Global View',
    title: 'Bring Global Conversations to One Room',
    lead: 'See what your audiences are feeling across regions, languages, and cultures — and act locally.',
    bullets: [
      'Use geo-visualizations to track location-specific buzz and sentiment',
      'Filter conversations by country, city, or region to understand local impact and nuances',
      'Detect regional trends, customer issues, or product feedback that need swift attention',
      'Tailor CX or marketing responses with localized context and actionable insights',
    ],
    visual: { Icon: Globe, title: 'Global Room', subtitle: 'Geo Buzz · Regional Sentiment', Bg: GlobeGridBg, tint: 'violet' },
  },
];

/* DRAFT — review */
const FAQS = [
  { q: 'What is the Locobuzz Command Centre?', a: 'The Command Centre is a real-time mission-control dashboard that unifies brand health, campaign performance, influencer activity, and crisis signals across every digital channel — giving teams one source of truth for fast, confident decisions.' },
  { q: 'How quickly can the Command Centre detect a crisis?', a: 'Automated thresholds and AI-powered sentiment triggers flag anomalies in under two minutes on average. Once an alert fires, the platform routes it to the right team and surfaces the origin and narrative spread for immediate action.' },
  { q: 'Can multiple teams — PR, CX, marketing — use it simultaneously?', a: 'Yes. The Command Centre is designed for cross-functional collaboration. PR, CX, marketing, and product teams all see the same live view, with role-based access and shared war-room screens for peak moments like launches or crises.' },
  { q: 'Does it integrate with our existing tools like CRM and analytics?', a: 'The Command Centre pulls in data from 30+ sources including social channels, Google Analytics, CRM platforms, chatbots, and app stores — so your team sees every signal, not just social, in one consolidated view.' },
  { q: 'Is the Command Centre suitable for enterprise NOCs and exec dashboards?', a: 'Absolutely. The platform is built for large-scale deployments including enterprise Network Operations Centres, C-suite executive dashboards, and live war rooms with voice-activation support for hands-free monitoring.' },
];

/* DRAFT — review */
const PANEL = {
  title: 'Command Centre',
  badge: 'Live ops',
  kpiValue: 3847,
  kpiLabel: 'Active alerts today',
  trend: [8, 14, 11, 19, 24, 20, 31, 28, 40, 48],
  trendDelta: '+38%',
  segments: [
    { label: 'On-track', pct: 62, tone: 'pos' },
    { label: 'Monitored', pct: 27, tone: 'neu' },
    { label: 'Critical', pct: 11, tone: 'neg' },
  ],
  feedLabel: 'Incoming alerts',
  feed: [
    { text: 'Sentiment spike — negative mentions up 34% in last hour', meta: 'Brand Watch · Tier 1', tag: 'Alert', tone: 'neg' },
    { text: 'Campaign #SpringLaunch hitting 2.1M reach ahead of target', meta: 'Campaign Console', tag: 'Goal', tone: 'pos' },
    { text: 'Influencer @priya_voices mentioned product with high reach', meta: 'Influencer Hub', tag: 'Watch', tone: 'neu' },
    { text: 'PR crisis threshold crossed — routing to response team', meta: 'Crisis Watch · Auto', tag: 'Critical', tone: 'neg' },
    { text: 'Competitor share of voice dropped 8 pts this week', meta: 'Competitor Intel', tag: 'Intel', tone: 'pos' },
  ],
  chip: 'AgentIQ routed 14 alerts to CX team · avg 90 s', /* DRAFT — review */
};

/* ── Reusable bits ──────────────────────────────────────────────────────── */
function IconTile({ Icon, tint, size = 'md' }) {
  const T = TINTS[tint];
  const dim = size === 'lg' ? 64 : 48;
  const iconSize = size === 'lg' ? 28 : 22;
  return (
    <div style={{ width: dim, height: dim, borderRadius: 14, background: T.bg, border: `1px solid ${T.stroke}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={iconSize} color={T.accent} strokeWidth={1.8} />
    </div>
  );
}

function VisualCard({ visual }) {
  const { t } = useTranslation();
  const T = TINTS[visual.tint];
  const { Bg, Icon } = visual;
  return (
    <div style={{ minHeight: 340, borderRadius: 'var(--radius-3xl)', padding: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: `linear-gradient(135deg, #ffffff 0%, ${T.bg} 100%)`, border: `1px solid ${T.stroke}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,152,255,0.06)', position: 'relative', overflow: 'hidden' }}>
      <Bg tint={T} />
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <IconTile Icon={Icon} tint={visual.tint} size="lg" />
        <div>
          <div style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 24, color: 'var(--text-heading)', lineHeight: 1.2, letterSpacing: '-0.015em' }}>{t(visual.title)}</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 6 }}>{t(visual.subtitle)}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */
export default function CommandCentrePage() {
  const { t } = useTranslation();
  return (
    <>
      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--gradient-hero)', position: 'relative', overflow: 'hidden', padding: 'var(--hero-pad)' }}>
        <div aria-hidden="true" className="cc-hero-orbs">
          <span className="cc-orb cc-orb1" />
          <span className="cc-orb cc-orb2" />
        </div>
        <InlineStyle css={`
          .cc-hero-orbs { position:absolute; inset:0; pointer-events:none; z-index:0; overflow:hidden; }
          .cc-orb { position:absolute; border-radius:50%; filter:blur(70px); }
          .cc-orb1 { width:680px; height:680px; top:-220px; left:6%; background:radial-gradient(circle at 40% 40%, rgba(0,152,255,0.22), rgba(0,152,255,0.05) 50%, transparent 72%); animation:ccOrb1 22s ease-in-out infinite; }
          .cc-orb2 { width:520px; height:520px; top:-120px; right:4%; background:radial-gradient(circle at 60% 30%, rgba(0,87,212,0.16), rgba(0,152,255,0.04) 55%, transparent 72%); animation:ccOrb2 26s ease-in-out infinite; }
          @keyframes ccOrb1 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(30px,-22px) scale(1.06);} }
          @keyframes ccOrb2 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(-26px,26px) scale(1.05);} }
          @media (prefers-reduced-motion: reduce){ .cc-orb{animation:none;} }
        `} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <nav aria-label={t('Command Centre')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20, padding: '8px 14px 8px 12px', borderRadius: 999, border: '1px solid var(--border-light)', background: 'var(--surface-page, #f5f7ff)', fontSize: 12.5, letterSpacing: '0.02em' }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.16)' }} />
            <Link href="/platform/" style={{ color: 'var(--text-muted)', fontWeight: 600, textDecoration: 'none' }}>{t('Platform')}</Link>
            <span aria-hidden="true" style={{ color: 'var(--text-muted)', opacity: 0.45, fontWeight: 600 }}>›</span>
            <span style={{ color: 'var(--brand)', fontWeight: 700 }}>{t('Command Centre')}</span>
          </nav>

          <div className="lb-hero-grid">
            <div>
              <span className="lb-pill" style={{ marginBottom: 16 }}>{t(HERO.eyebrow)}</span>
              <motion.h1
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.05 }}
                style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'var(--type-hero-title)', color: 'var(--text-heading)', lineHeight: 1.08, letterSpacing: '-0.02em', margin: '0 0 14px' }}
              >
                {t('Social Media Digital')} <span className="serif-accent italic">{t('Command Centre')}</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.12 }}
                style={{ fontSize: 'clamp(17px, 1.6vw, 20px)', color: 'var(--brand)', fontWeight: 600, margin: '0 0 18px', lineHeight: 1.4 }}
              >
                {t(HERO.tagline)}
              </motion.p>
              {HERO.body.map((p, i) => (
                <p key={i} style={{ fontSize: 15.5, color: 'var(--text-body)', lineHeight: 1.75, margin: i === 0 ? '0 0 12px' : '0 0 28px' }}>{t(p)}</p>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.2 }}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
              >
                <BookDemoButton label={t('Book a Demo')} />
                <Button href="/platform/" variant="secondary" size="lg">{t('Explore Platform')}</Button>
              </motion.div>
            </div>

            <PlatformHeroPanel {...PANEL} />
          </div>
        </div>
      </section>

      {/* ── DARK IMPACT BAND ───────────────────────────────────────────── */}
      {/* DRAFT — review */}
      <section style={{ padding: 'var(--section-pad)' }}>
        <div className="container">
          <MetricsBand items={IMPACT} columns={4} />
        </div>
      </section>

      {/* ── THE STACK ──────────────────────────────────────────────────── */}
      <section className="lb-section-alt">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center', marginBottom: 48 }}>
            <span className="lb-pill" style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={12} color="var(--brand)" strokeWidth={2.2} />
              {t('Why It Matters')}
            </span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>{t('Why High-Performance Brands Count on the')} <span className="serif-accent italic">{t('Command Centre')}</span></h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 760, margin: '0 auto', lineHeight: 1.7 }}>{t('Visualize the entire omnichannel CX ecosystem. From fast-moving campaigns to digital crises, the Command Centre ensures your brand never misses a beat — designed for enterprise NOCs and executive dashboards, with real-time visibility into brand health, sentiment, and campaign ROI.')}</p>
          </motion.div>

          <div className="lb-grid-3">
            {STACK_POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                className="lb-card"
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ ...spring, delay: (i % 3) * 0.08 }}
                whileHover={{ y: -6 }}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <IconTile Icon={p.Icon} tint={p.tint} />
                <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 700, fontSize: 17, color: 'var(--text-heading)', lineHeight: 1.35, margin: '14px 0 8px' }}>{t(p.title)}</h3>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{t(p.text)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEEP DIVES intro ───────────────────────────────────────────── */}
      <section className="lb-section" style={{ paddingBottom: 8 }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center' }}>
            <span className="lb-pill" style={{ marginBottom: 14 }}>{t('The Intelligence Engine')}</span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>{t('The Intelligence Engine Behind')} <span className="serif-accent italic">{t('Command Centre')}</span></h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 740, margin: '0 auto', lineHeight: 1.7 }}>{t('AI-driven insights with rich visualizations — pulled from social, CRM, analytics, and support systems in real time.')}</p>
          </motion.div>
        </div>
      </section>

      {DEEP_DIVES.map((d, i) => {
        const tone = d.dark
          ? { eyebrow: '#7FB3FF', title: '#ffffff', lead: 'rgba(255,255,255,0.82)', bullet: 'rgba(255,255,255,0.80)', checkBg: 'rgba(127,179,255,0.16)', checkBorder: 'rgba(127,179,255,0.4)', check: '#7FB3FF' }
          : { eyebrow: 'var(--brand)', title: 'var(--text-heading)', lead: 'var(--text-body)', bullet: 'var(--text-secondary)', checkBg: 'var(--brand-subtle)', checkBorder: '#BDD9FF', check: 'var(--brand)' };
        const bg = d.dark ? 'linear-gradient(160deg, #0D1B3E 0%, #0A1454 100%)' : (i % 2 === 0 ? '#ffffff' : '#F8FAFF');
        return (
          <section key={d.title} style={{ background: bg, padding: '72px 0', position: 'relative', overflow: 'hidden' }}>
            <div className="container">
              <div className="lb-hero-grid" style={{ paddingBottom: 0 }}>
                <motion.div variants={d.reverse ? slideInRight : slideInLeft} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ order: d.reverse ? 2 : 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: tone.eyebrow, letterSpacing: '1.8px', textTransform: 'uppercase', marginBottom: 12 }}>{t(d.eyebrow)}</div>
                  <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(24px, 3vw, 34px)', color: tone.title, lineHeight: 1.18, letterSpacing: '-0.015em', margin: '0 0 14px' }}>{t(d.title)}</h3>
                  <p style={{ fontSize: 15.5, color: tone.lead, lineHeight: 1.75, margin: '0 0 24px' }}>{t(d.lead)}</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {d.bullets.map((b, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <span aria-hidden="true" style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: tone.checkBg, border: `1.5px solid ${tone.checkBorder}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 3 }}>
                          <Check size={11} color={tone.check} strokeWidth={3} />
                        </span>
                        <span style={{ fontSize: 15, color: tone.bullet, lineHeight: 1.65 }}>{t(b)}</span>
                      </li>
                    ))}
                  </ul>
                  {d.cta && (
                    <div style={{ marginTop: 28 }}>
                      <Button href={d.cta.href} variant="cta" size="lg">{t(d.cta.label)}</Button>
                    </div>
                  )}
                </motion.div>

                <motion.div variants={scaleIn} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ order: d.reverse ? 1 : 2 }}>
                  <VisualCard visual={d.visual} />
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* ── SOCIAL PROOF ───────────────────────────────────────────────── */}

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      {/* DRAFT — review */}
      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={<>{t('faq.everything_you_need_to')}{' '}<span className="serif-accent italic">{t('faq.know')}</span></>}
          align="center"
        />
        <FaqAccordion items={FAQS.map((f) => ({ q: t(f.q), a: t(f.a) }))} columns={2} name="command-centre-faq" />
      </PageSection>

      <BookDemoSection />
    </>
  );
}
